import React, { 
  useState, 
  useEffect, 
  useRef, 
  useCallback,
  
} from 'react';
import PropTypes from 'prop-types';
import './Autocomplete.scss'

const AutocompleteTPO = ({
  options,
  getOptionLabel,
  value,
  onChange,
  filterOptions,
  asyncLoadOptions,
  debounceDelay = 300,
  highlightMatches = true,
  loadingText = 'Loading...',
  noOptionsText = 'Not matches',
  renderOption,
  sx = {},
  animations = true,
  placeholder = 'Choose...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const optionsListRef = useRef(null);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Processing clicks outside the component
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeOptions();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = useCallback((e) => {
    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          Math.min(prev + 1, filteredOptions.length - 1)
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          handleSelect(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        closeOptions();
        break;
      default:
        break;
    }
  }, [filteredOptions, selectedIndex]);

  // Scroll to active element
  useEffect(() => {
    if (optionsListRef.current && selectedIndex >= 0) {
      const activeItem = optionsListRef.current.children[selectedIndex];
      activeItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Asynchronous download with debounce and cancellation of previous requests
  const loadOptions = useCallback(async (searchValue) => {
    if (!asyncLoadOptions) return;

    try {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      const data = await asyncLoadOptions(
        searchValue, 
        abortControllerRef.current.signal
      );
      setFilteredOptions(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error download:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [asyncLoadOptions]);

  // Filter options
  useEffect(() => {
    if (asyncLoadOptions) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        loadOptions(inputValue);
      }, debounceDelay);
    } else {
      setFilteredOptions(filterOptions(options, { inputValue }));
    }
  }, [inputValue, options, filterOptions, asyncLoadOptions, debounceDelay, loadOptions]);

  // Highlight Matches in options
  const highlightMatch = useCallback((text, query) => {
    if (!highlightMatches || !query.trim()) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    return text.split(regex).map((part, i) => 
      regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
  }, [highlightMatches]);


  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setShowOptions(true);
    setSelectedIndex(-1);
  };

  const handleSelect = (option) => {
    onChange(option);
    setInputValue(getOptionLabel(option));
    closeOptions();
  };

  const closeOptions = () => {
    setShowOptions(false);
    setSelectedIndex(-1);
  };

  const renderOptionItem = useCallback((option, index) => {
    if (renderOption) {
      return renderOption({
        option,
        index,
        inputValue,
        isSelected: index === selectedIndex,
        handlers: { handleSelect }
      });
    }

    return (
      <li
        key={option.id}
        onClick={() => handleSelect(option)}
        className={`autocomplete-option ${index === selectedIndex ? 'selected' : ''}`}
        style={sx.option}
        role="option"
        aria-selected={index === selectedIndex}
      >
        {highlightMatch(getOptionLabel(option), inputValue)}
      </li>
    );
  }, [renderOption, inputValue, selectedIndex, sx.option, highlightMatch, handleSelect]);

  return (
    <div 
      ref={wrapperRef}
      className="autocomplete-wrapper"
      style={sx.wrapper}
      role="combobox"
      aria-expanded={showOptions}
      aria-haspopup="listbox"
    >
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowOptions(true)}
        className={`autocomplete-input ${animations ? 'animated' : ''}`}
        placeholder={placeholder}
        style={sx.input}
        aria-autocomplete="list"
        aria-controls="autocomplete-options"
      />

      {loading && (
        <div 
          className="autocomplete-loading"
          style={sx.loading}
          role="status"
          aria-live="polite"
        >
          {loadingText}
        </div>
      )}

      {showOptions && (
        <ul
          ref={optionsListRef}
          id="autocomplete-options"
          className={`autocomplete-options ${animations ? 'slide-fade' : ''}`}
          style={sx.options}
          role="listbox"
        >
          {filteredOptions.map((option, index) => 
            renderOptionItem(option, index)
          )}

          {!loading && filteredOptions.length === 0 && (
            <li 
              className="autocomplete-no-options"
              style={sx.noOptions}
              role="option"
            >
              {noOptionsText}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

AutocompleteTPO.propTypes = {
  options: PropTypes.array.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  filterOptions: PropTypes.func,
  asyncLoadOptions: PropTypes.func,
  debounceDelay: PropTypes.number,
  highlightMatches: PropTypes.bool,
  loadingText: PropTypes.string,
  noOptionsText: PropTypes.string,
  renderOption: PropTypes.func,
  sx: PropTypes.shape({
    wrapper: PropTypes.object,
    input: PropTypes.object,
    options: PropTypes.object,
    option: PropTypes.object,
    loading: PropTypes.object,
    noOptions: PropTypes.object,
  }),
  animations: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default AutocompleteTPO;